/* ==========================================================================
   Header behaviour
   - sticky shell that hides on scroll down and reveals on scroll up
   - hover-intent mega menus with a page scrim
   - transparent-over-hero header that turns solid on hover / scroll
   - mobile drawer with accordion categories, and a search overlay
   No dependencies. Re-initialises cleanly for the Shopify theme editor.
   ========================================================================== */
(function () {
  'use strict';

  var OPEN_DELAY = 100;   // hover intent before a mega menu opens
  var CLOSE_DELAY = 180;  // grace period when the pointer leaves the header
  var SCROLL_THRESHOLD = 8;

  function DsHeader(root) {
    this.root = root;
    this.sticky = root.querySelector('[data-shp-sticky]');
    this.navItems = Array.prototype.slice.call(root.querySelectorAll('[data-shp-nav-item]'));
    this.drawer = root.querySelector('[data-shp-drawer]');
    this.searchPanel = root.querySelector('[data-shp-search-panel]');
    this.overlay = root.querySelector('[data-shp-overlay]');

    this.isOverlay = root.getAttribute('data-overlay') === 'true';
    this.openTimer = null;
    this.closeTimer = null;
    this.lastY = window.pageYOffset;
    this.ticking = false;
    this.openItem = null;

    this.bound = {
      scroll: this.onScroll.bind(this),
      keydown: this.onKeydown.bind(this),
      resize: this.onResize.bind(this)
    };

    this.init();
  }

  DsHeader.prototype.init = function () {
    this.setSolid(!this.isOverlay || window.pageYOffset > SCROLL_THRESHOLD);
    this.bindMegaMenus();
    this.bindShellInvert();
    this.bindDrawer();
    this.bindSearch();
    this.startBanner();
    this.bindDrawerBanner();
    this.positionScrim();

    window.addEventListener('scroll', this.bound.scroll, { passive: true });
    window.addEventListener('resize', this.bound.resize, { passive: true });
    document.addEventListener('keydown', this.bound.keydown);
  };

  DsHeader.prototype.destroy = function () {
    window.removeEventListener('scroll', this.bound.scroll);
    window.removeEventListener('resize', this.bound.resize);
    document.removeEventListener('keydown', this.bound.keydown);
    window.clearInterval(this.bannerTimer);
    if (this.bannerVisibility) document.removeEventListener('visibilitychange', this.bannerVisibility);
    window.clearInterval(this.bannerAutoTimer);
    window.clearInterval(this.bannerTweenTimer);
    document.body.classList.remove('shp-locked');
  };

  /* ---- announcement rotation -------------------------------------------- */

  DsHeader.prototype.startBanner = function () {
    var slides = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-shp-banner-slide]')
    );
    if (slides.length < 2) return;

    var self = this;
    var i = slides.findIndex(function (s) { return s.getAttribute('data-active') === 'true'; });
    if (i < 0) { i = 0; slides[0].setAttribute('data-active', 'true'); }

    function step() {
      slides[i].setAttribute('data-active', 'false');
      i = (i + 1) % slides.length;
      slides[i].setAttribute('data-active', 'true');
    }

    function run() {
      window.clearInterval(self.bannerTimer);
      self.bannerTimer = window.setInterval(step, 5000);
    }

    run();

    /* A background tab throttles timers and then lets them fire in a burst on
       the way back, which walks the banner through several messages at once.
       Stop while it is away and start the count again on return. */
    this.bannerVisibility = function () {
      if (document.hidden) window.clearInterval(self.bannerTimer);
      else run();
    };

    document.addEventListener('visibilitychange', this.bannerVisibility);
  };

  /* ---- state helpers ---------------------------------------------------- */

  DsHeader.prototype.setSolid = function (on) {
    this.root.setAttribute('data-solid', on ? 'true' : 'false');
  };

  DsHeader.prototype.setHidden = function (on) {
    this.root.setAttribute('data-hidden', on ? 'true' : 'false');
  };

  // The scrim starts below the header so the menu itself is never dimmed.
  DsHeader.prototype.positionScrim = function () {
    var scrim = this.root.querySelector('[data-shp-scrim]');
    if (!scrim || !this.sticky) return;
    scrim.style.top = this.sticky.getBoundingClientRect().height + 'px';
  };

  /* ---- scroll ----------------------------------------------------------- */

  DsHeader.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    var self = this;
    window.requestAnimationFrame(function () {
      self.updateOnScroll();
      self.ticking = false;
    });
  };

  DsHeader.prototype.updateOnScroll = function () {
    var y = Math.max(0, window.pageYOffset);
    var delta = y - this.lastY;

    if (!this.isOverlay || y > SCROLL_THRESHOLD || this.openItem) {
      this.setSolid(true);
    } else {
      this.setSolid(false);
    }

    // Never hide while a menu or drawer is open, or near the very top.
    var locked = this.openItem ||
      this.root.getAttribute('data-drawer-open') === 'true' ||
      this.root.getAttribute('data-search-open') === 'true';

    if (locked || y <= this.stickyHeight()) {
      this.setHidden(false);
    } else if (Math.abs(delta) > 4) {
      this.setHidden(delta > 0);
    }

    this.lastY = y;
  };

  DsHeader.prototype.stickyHeight = function () {
    return this.sticky ? this.sticky.getBoundingClientRect().height : 0;
  };

  DsHeader.prototype.onResize = function () {
    this.positionScrim();
    if (window.innerWidth >= 1024) this.closeDrawer();
  };

  /* ---- mega menus ------------------------------------------------------- */

  DsHeader.prototype.bindMegaMenus = function () {
    var self = this;

    this.navItems.forEach(function (item) {
      var link = item.querySelector('[data-shp-nav-link]');
      var mega = item.querySelector('[data-shp-mega]');
      if (!mega) return;

      item.addEventListener('mouseenter', function () {
        self.scheduleOpen(item);
      });

      item.addEventListener('mouseleave', function () {
        self.scheduleClose();
      });

      // Keyboard: opening on focus keeps the panel reachable without a pointer.
      if (link) {
        link.addEventListener('focus', function () { self.open(item); });
      }

      mega.addEventListener('focusin', function () { self.open(item); });

      mega.addEventListener('focusout', function (e) {
        if (!mega.contains(e.relatedTarget) && !item.contains(e.relatedTarget)) {
          self.close();
        }
      });
    });

    // Leaving the whole header closes; re-entering cancels a pending close.
    if (this.sticky) {
      this.sticky.addEventListener('mouseenter', function () { self.cancelClose(); });
      this.sticky.addEventListener('mouseleave', function () { self.scheduleClose(); });
    }
  };

  DsHeader.prototype.scheduleOpen = function (item) {
    var self = this;
    this.cancelClose();
    window.clearTimeout(this.openTimer);
    // An already-open menu switches instantly; the first one waits for intent.
    if (this.openItem) { this.open(item); return; }
    this.openTimer = window.setTimeout(function () { self.open(item); }, OPEN_DELAY);
  };

  DsHeader.prototype.scheduleClose = function () {
    var self = this;
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(function () { self.close(); }, CLOSE_DELAY);
  };

  DsHeader.prototype.cancelClose = function () {
    window.clearTimeout(this.closeTimer);
  };

  DsHeader.prototype.open = function (item) {
    if (this.openItem === item) return;
    this.cancelClose();
    if (this.openItem) this.setItemOpen(this.openItem, false);
    this.openItem = item;
    this.setItemOpen(item, true);
    this.root.setAttribute('data-menu-open', 'true');
    this.setSolid(true);
    this.setHidden(false);
    this.positionScrim();
  };

  DsHeader.prototype.close = function () {
    window.clearTimeout(this.openTimer);
    if (this.openItem) this.setItemOpen(this.openItem, false);
    this.openItem = null;
    this.root.setAttribute('data-menu-open', 'false');
    if (this.isOverlay && window.pageYOffset <= SCROLL_THRESHOLD) this.setSolid(false);
  };

  DsHeader.prototype.setItemOpen = function (item, on) {
    item.setAttribute('data-open', on ? 'true' : 'false');
    var link = item.querySelector('[data-shp-nav-link]');
    if (link) link.setAttribute('aria-expanded', on ? 'true' : 'false');
  };

  /* ---- drawer banner ---------------------------------------------------- */

  // The strip is CSS scroll-snap, so a swipe is native. This keeps the progress
  // segments in step and advances it on a timer while the drawer is open.
  DsHeader.prototype.bindDrawerBanner = function () {
    var self = this;
    var track = this.root.querySelector('[data-shp-dbanner-track]');
    if (!track) return;
    this.bannerTrack = track;

    this.bannerBars = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-shp-dbanner-bar]')
    );

    this.slideCount = track.children.length;
    this.slideIndex = 0;

    // Scroll events only correct the index after a manual swipe; the timer
    // sets it itself, because scroll events don't fire dependably while the
    // page isn't painting and the carousel would stall on slide two.
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.setTimeout(function () {
        self.setActiveSlide(
          Math.round(track.scrollLeft / Math.max(1, track.clientWidth))
        );
        ticking = false;
      }, 80);
    }, { passive: true });

    // A manual swipe takes over: hold the timer off, then resume.
    ['pointerdown', 'touchstart'].forEach(function (evt) {
      track.addEventListener(evt, function () { self.stopDrawerBannerAuto(); }, { passive: true });
    });
    ['pointerup', 'touchend', 'mouseleave'].forEach(function (evt) {
      track.addEventListener(evt, function () { self.startDrawerBannerAuto(); }, { passive: true });
    });

    this.setActiveSlide(0);
  };

  DsHeader.prototype.setActiveSlide = function (i) {
    i = Math.max(0, Math.min((this.slideCount || 1) - 1, i));
    this.slideIndex = i;
    (this.bannerBars || []).forEach(function (b, n) {
      b.setAttribute('data-active', n === i ? 'true' : 'false');
    });
  };

  DsHeader.prototype.startDrawerBannerAuto = function () {
    var self = this;
    var track = this.bannerTrack;
    if (!track || this.slideCount < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.stopDrawerBannerAuto();
    this.bannerAutoTimer = window.setInterval(function () {
      self.slideBannerTo(((self.slideIndex || 0) + 1) % self.slideCount);
    }, 5000);
  };

  // Hand-rolled tween rather than scrollTo({behavior:'smooth'}): mandatory
  // scroll-snap cancels programmatic smooth scrolls, and this also lets the
  // glide be deliberately slow. Snap is lifted for the duration, then restored
  // so a real swipe still snaps.
  DsHeader.prototype.slideBannerTo = function (index) {
    var track = this.bannerTrack;
    if (!track) return;

    var slide = track.children[index];
    if (!slide) return;

    var from = track.scrollLeft;
    var to = from + (slide.getBoundingClientRect().left - track.getBoundingClientRect().left);

    // Claim the index up front so the next tick advances from here even if no
    // scroll event ever lands.
    this.setActiveSlide(index);
    if (Math.abs(to - from) < 1) return;

    var self = this;
    window.clearInterval(this.bannerTweenTimer);

    var prevSnap = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';

    var duration = 700;
    var began = Date.now();

    var ease = function (p) {
      return p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
    };

    // Stepped on a timer rather than requestAnimationFrame: rAF is suspended
    // whenever the page isn't being painted, and a half-finished tween would
    // otherwise leave scroll-snap switched off for good.
    // Progress is measured against the wall clock, never against the number of
    // ticks — browsers throttle timers (to ~1s in a background tab), so
    // accumulating a fixed step stretches a 700ms glide into a minute-long crawl.
    this.bannerTweenTimer = window.setInterval(function () {
      var p = Math.min(1, (Date.now() - began) / duration);
      track.scrollLeft = from + (to - from) * ease(p);
      if (p >= 1) {
        window.clearInterval(self.bannerTweenTimer);
        track.style.scrollSnapType = prevSnap;
      }
    }, 16);
  };

  DsHeader.prototype.stopDrawerBannerAuto = function () {
    window.clearInterval(this.bannerAutoTimer);
  };

  /* ---- shell invert ----------------------------------------------------- */

  // Hovering the society link turns the entire shell light — the announcement
  // bar too, which a category hover deliberately leaves dark.
  DsHeader.prototype.bindShellInvert = function () {
    var self = this;

    this.root.querySelectorAll('[data-shp-shell-invert]').forEach(function (el) {
      // Only the flag — the wipe provides the white and the CSS flips the text
      // off the same flag. Touching data-solid here would fade the backdrop in
      // on its own timing and race the wipe, which read as an instant snap.
      var on = function () {
        self.root.setAttribute('data-shell-light', 'true');
      };
      var off = function () {
        self.root.setAttribute('data-shell-light', 'false');
      };

      el.addEventListener('mouseenter', on);
      el.addEventListener('focus', on);
      el.addEventListener('mouseleave', off);
      el.addEventListener('blur', off);
    });
  };

  /* ---- mobile drawer ---------------------------------------------------- */

  DsHeader.prototype.bindDrawer = function () {
    var self = this;

    this.root.querySelectorAll('[data-shp-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.openDrawer(); });
    });

    this.root.querySelectorAll('[data-shp-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.closeDrawer(); });
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self.closeDrawer();
        self.closeSearch();
      });
    }

    // Level 3: plus/minus groups inside a panel
    this.root.querySelectorAll('[data-shp-l2-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var group = trigger.closest('[data-shp-l2]');
        if (!group) return;
        var isOpen = group.getAttribute('data-open') === 'true';
        group.setAttribute('data-open', isOpen ? 'false' : 'true');
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    });

    // Level 2: drill into a category
    this.root.querySelectorAll('[data-shp-panel-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.openPanel(btn.getAttribute('data-shp-panel-open'));
      });
    });

    this.root.querySelectorAll('[data-shp-panel-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.closePanel(); });
    });
  };

  /* ---- level 2 panels --------------------------------------------------- */

  DsHeader.prototype.openPanel = function (key) {
    var panel = this.root.querySelector('[data-shp-panel="' + key + '"]');
    if (!panel) return;

    this.closePanel();

    panel.setAttribute('data-open', 'true');
    panel.setAttribute('aria-hidden', 'false');
    // Elk bezoek begint bovenaan; anders staat een tweede bezoek nog op de plek
    // waar je de vorige keer was blijven hangen.
    panel.scrollTop = 0;
    panel.scrollTop = 0;

    var title = this.root.querySelector('[data-shp-drawer-title]');
    if (title) title.textContent = panel.getAttribute('data-title') || '';

    this.root.setAttribute('data-panel-open', 'true');
    this.openPanelEl = panel;

    // The list behind it keeps its own scroll position for the way back.
    var body = this.root.querySelector('.shp__drawer-body');
    if (body) this.listScrollTop = body.scrollTop;
  };

  DsHeader.prototype.closePanel = function () {
    if (this.openPanelEl) {
      this.openPanelEl.setAttribute('data-open', 'false');
      this.openPanelEl.setAttribute('aria-hidden', 'true');
      // Leave the panel collapsed so a second visit starts from the group list
      // rather than wherever the last visit was left open.
      this.openPanelEl.querySelectorAll('[data-shp-l2]').forEach(function (group) {
        group.setAttribute('data-open', 'false');
        var trigger = group.querySelector('[data-shp-l2-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      this.openPanelEl = null;
    }
    this.root.setAttribute('data-panel-open', 'false');

    var body = this.root.querySelector('.shp__drawer-body');
    if (body && this.listScrollTop != null) body.scrollTop = this.listScrollTop;
    this.listScrollTop = null;
  };

  DsHeader.prototype.openDrawer = function () {
    this.root.setAttribute('data-drawer-open', 'true');
    document.body.classList.add('shp-locked');
    this.setHidden(false);
    // Only cycle while the drawer is actually on screen.
    this.startDrawerBannerAuto();
    var first = this.drawer && this.drawer.querySelector('button, a');
    if (first) first.focus();
  };

  DsHeader.prototype.closeDrawer = function () {
    if (this.root.getAttribute('data-drawer-open') !== 'true') return;
    this.root.setAttribute('data-drawer-open', 'false');
    this.closePanel();
    this.stopDrawerBannerAuto();
    if (this.root.getAttribute('data-search-open') !== 'true') {
      document.body.classList.remove('shp-locked');
    }
  };

  /* ---- search ----------------------------------------------------------- */

  DsHeader.prototype.bindSearch = function () {
    var self = this;

    this.root.querySelectorAll('[data-shp-search-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.openSearch(); });
    });

    // The same button closes it again, which is what the cross is promising.
    this.root.querySelectorAll('[data-shp-search-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = self.root.getAttribute('data-search-open') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (open) self.closeSearch(); else self.openSearch();
      });
    });

    this.root.querySelectorAll('[data-shp-search-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.closeSearch(); });
    });

    // Focusing the desktop field forces the solid state so the input is legible.
    var deskInput = this.root.querySelector('[data-shp-search-input]');
    if (deskInput) {
      deskInput.addEventListener('focus', function () { self.setSolid(true); });
      deskInput.addEventListener('blur', function () {
        if (self.isOverlay && window.pageYOffset <= SCROLL_THRESHOLD && !self.openItem) {
          self.setSolid(false);
        }
      });
    }

    this.bindFind();
  };

  /* ---- results while typing ----------------------------------------------

     Shopify's predictive search endpoint will render a section for us, so the
     markup for a result is Liquid — the same card the rest of the build uses —
     rather than something written a second time in JavaScript. */

  DsHeader.prototype.bindFind = function () {
    var self = this;
    this.find = this.root.querySelector('[data-shp-find]');
    var inputs = [].slice.call(this.root.querySelectorAll('[data-shp-search-input]'));
    if (!this.find || !inputs.length) return;

    // One panel, either field. The phone's field lives under the bar and the
    // desktop one in it, but they ask the same question and show the same answer.
    var input = inputs[0];

    var clear = this.root.querySelector('[data-shp-search-clear]');
    var rest = this.find.querySelector('[data-shp-find-rest]');
    var live = this.find.querySelector('[data-shp-find-live]');
    var chips = this.find.querySelector('[data-shp-find-chips]');
    var timer = null;
    var last = '';
    var seq = 0;

    function show(open) {
      self.find.setAttribute('data-open', String(open));
      inputs.forEach(function (field) { field.setAttribute('aria-expanded', String(open)); });
      if (open && window.DsHome && window.DsHome.resetRails) {
        window.DsHome.resetRails(self.find);
      }
    }

    // So the search button can open the panel without going through a field.
    this.showFind = function () {
      var term = input.value.trim();
      face(term.length >= 2 && live && live.innerHTML ? 'live' : 'rest');
      show(true);
    };

    // Before anything is typed the panel shows the popular searches and the
    // trending row, which are already in the page. Typing swaps to results.
    function face(which) {
      if (rest) rest.hidden = which !== 'rest';
      if (live) live.hidden = which !== 'live';
      // Popular searches are for the resting panel only. Once someone is typing,
      // the top of the panel belongs to Shopify's own suggestions for that word,
      // or to nothing at all.
      if (chips) chips.hidden = which !== 'rest';
    }

    function ask(term) {
      // Every keystroke would otherwise be a request; wait for a pause instead.
      var mine = ++seq;
      var url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      fetch(url + 'search/suggest?q=' + encodeURIComponent(term) +
        '&section_id=ds-predictive-search' +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=10', { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          // An answer that arrived after a later one was asked for is stale.
          if (mine !== seq) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-shp-find-inner]');
          if (live) live.innerHTML = fresh ? fresh.outerHTML : '';
          face('live');
          show(true);
          if (window.DsHome && window.DsHome.boot) window.DsHome.boot(self.find);
        })
        .catch(function () { show(false); });
    }

    function onType(field) {
      // Keep the other field in step, so closing one and opening the other does
      // not lose what was typed.
      inputs.forEach(function (other) { if (other !== field) other.value = field.value; });
      input = field;
      var term = field.value.trim();
      if (clear) clear.hidden = !term;
      if (timer) clearTimeout(timer);

      // Too short to search on, but the popular searches still belong on screen.
      if (term.length < 2) { last = ''; face('rest'); show(true); return; }

      // Same word as last time: nothing to fetch, but the panel may have been
      // put away since, so bring back what is already there.
      if (term === last) { face('live'); show(true); return; }

      timer = setTimeout(function () { last = term; ask(term); }, 200);
    }

    inputs.forEach(function (field) {
      field.addEventListener('input', function () { onType(field); });

      // Clicking into a field opens the panel straight away, as theirs does.
      field.addEventListener('focus', function () {
        input = field;
        var term = field.value.trim();
        face(term.length >= 2 && live && live.innerHTML ? 'live' : 'rest');
        show(true);
      });
    });

    if (clear) {
      clear.addEventListener('click', function () {
        inputs.forEach(function (field) { field.value = ''; });
        clear.hidden = true;
        last = '';
        face('rest');
        show(true);
        input.focus();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') show(false);
    });

    // A click anywhere outside the field or the panel puts it away.
    document.addEventListener('click', function (e) {
      if (self.find.getAttribute('data-open') !== 'true') return;
      if (self.find.contains(e.target)) return;
      var inside = inputs.some(function (field) {
        var form = field.closest('form');
        return field === e.target || (form && form.contains(e.target));
      });
      if (inside) return;

      // The button that opens the panel is outside both, and its own click
      // reaches this handler — without this it closed what it had just opened.
      if (e.target.closest && e.target.closest('[data-shp-search-open], [data-shp-search-toggle]')) return;
      show(false);
    });
  };

  DsHeader.prototype.openSearch = function () {
    this.root.setAttribute('data-search-open', 'true');
    // No page lock: the header stays put and the results scroll on their own, so
    // there is nothing to stop the page doing what it would normally do.

    // Show what the panel already holds straight away rather than waiting for
    // the field to take focus — on a phone that focus does not always land, and
    // the popular searches should be there the moment the field is.
    if (this.showFind) this.showFind();

    var input = this.searchPanel && this.searchPanel.querySelector('input');
    if (input) input.focus();
  };

  DsHeader.prototype.closeSearch = function () {
    if (this.root.getAttribute('data-search-open') !== 'true') return;
    this.root.setAttribute('data-search-open', 'false');
    if (this.root.getAttribute('data-drawer-open') !== 'true') {
      document.body.classList.remove('shp-locked');
    }
    // Closing the field closes what it was showing.
    if (this.find) this.find.setAttribute('data-open', 'false');
  };

  /* ---- keyboard --------------------------------------------------------- */

  DsHeader.prototype.onKeydown = function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    // Inside a category, Escape steps back a level rather than discarding the
    // whole drawer.
    if (this.openPanelEl) { this.closePanel(); return; }
    this.close();
    this.closeDrawer();
    this.closeSearch();
  };

  /* ---- boot ------------------------------------------------------------- */

  var instances = [];

  function boot(scope) {
    (scope || document).querySelectorAll('[data-shp-header]').forEach(function (root) {
      if (root.__shp) return;
      root.__shp = new DsHeader(root);
      instances.push(root.__shp);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Shopify theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-shp-header]');
    if (root && root.__shp) { root.__shp.destroy(); root.__shp = null; }
    boot(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-shp-header]');
    if (root && root.__shp) { root.__shp.destroy(); root.__shp = null; }
  });
})();
